// SpecialFeeui.tsx
import * as React from "react";
import { useMemo } from "react";
import {
  getActiveTaxType,
  calcSpecialFeeTax as calcFeeTax,
} from "components/Tax/manualtax";
import { AppIcon } from "components/AppIcon";
import { safeConsole } from "utils/envUtils";
import { readResolveTaxable, resolveTaxableFromSources } from "orderResolve/utils/readResolveTaxable";

/* ===================== External Types (exposed) ===================== */

export type SpecialFeeRow = {
  specialfee_id: string;
  specialfee_name: string;
  specialfee_amount: number;
  specialfee_tax_percentage: number;
  specialfee_tax_amount: number;
  specialfee_taxable: boolean;
  required: boolean;
};

export type FeePayload = {
  specialfee: SpecialFeeRow[];     // <-- ONLY currently selected fees
  selected_fee_ids: string[];
  total_amount: number;
  total_tax_amount: number;
  total_tax_percentage: number;    // aggregate if you want (we leave 0)
};

export type SpecialFeePickerV2Handle = {
  /** Latest payload (selected only) */
  getPayload(): FeePayload;
  /** Programmatically select by ids or names (names are resolved) */
  setSelectedIds(idsOrNames: string[]): void;
};


/* ===================== Internal UI Types ===================== */

export type UiFee = {
  id: string;
  title: string;
  amount: number;
  taxable: boolean;
  required: boolean;
  note?: string;
  status?: boolean;
  supplierId?:string
};

type Props = {
  token: string;
  API?: string;
  personId: string;             // createdBy or tenant/user id
  shippingAddress: any | null;  // used for tax; may be null
  knownTaxType?: "manual" | "taxcloud" | "geera";
  className?: string;
 supplierId?:string
 taxStatus?:string
  /** Optional: pass UI-ready fees directly (so we don't fetch) */
  fees?: UiFee[] | null;
  isTaxable?: boolean;  // defaults true
  statusOk?: boolean;
  /** Preselect from Edit: ids or names */
  initialSelectedIds?: string[] | null;

  /** Notify parent */
  onPayloadChange?: (payload: FeePayload) => void;
  onBusyChange?: (busy: boolean) => void; // ✅ Add busy state callback
  /** Raw vendor special-fee catalog after GET — for resolve payload taxable source of truth */
  onSourceFeesLoaded?: (fees: unknown[]) => void;
};

const SpecialFeePicker = React.forwardRef<SpecialFeePickerV2Handle, Props>(
  function SpecialFeePicker(
    {
      token,
      API = process.env.REACT_APP_API_URL || "",
      personId,
      supplierId,
      shippingAddress,
      knownTaxType,
      taxStatus,
      className = "",
      fees,
      isTaxable = true,
      statusOk = true,
      initialSelectedIds,
      onPayloadChange,
      onBusyChange, // ✅ Destructure busy callback
      onSourceFeesLoaded,
    },
    ref
  ) {
    console.log(supplierId,"supplierId in special fee")
    safeConsole.log("SpecialFeePicker props:", { token, API, personId, shippingAddress, knownTaxType, fees, isTaxable, statusOk, initialSelectedIds });
    // ✅ Use refs to store callbacks to prevent infinite loops
    const onBusyChangeRef = React.useRef(onBusyChange);
    const onPayloadChangeRef = React.useRef(onPayloadChange);
    const onSourceFeesLoadedRef = React.useRef(onSourceFeesLoaded);
    
    React.useEffect(() => {
      onBusyChangeRef.current = onBusyChange;
      onPayloadChangeRef.current = onPayloadChange;
      onSourceFeesLoadedRef.current = onSourceFeesLoaded;
    });
    
    const [loading, setLoading] = React.useState(false);
    const [uiFees, setUiFees] = React.useState<UiFee[]>([]);
    const [selected, setSelected] = React.useState<string[]>([]);
    const [rows, setRows] = React.useState<SpecialFeeRow[]>([]);
    const [raw, setRaw] = React.useState<any[]>([]);
    const [getstatusTaxType, setGetStatusTaxType] = React.useState("");
    // ===== helpers =====
    const asNum = (v: any) => Number(v || 0);
    const money = (n: any) => `$${asNum(n).toFixed(2)}`;

    const emit = React.useCallback(
      (r: SpecialFeeRow[], ids: string[]) => {
        const payload: FeePayload = {
          specialfee: r,                   // <-- SELECTED ONLY
          selected_fee_ids: ids,
          total_amount: r.reduce((s, x) => s + asNum(x.specialfee_amount), 0),
          total_tax_amount: r.reduce((s, x) => s + asNum(x.specialfee_tax_amount), 0),
          total_tax_percentage: 0,         // keep 0; aggregate percent is optional
        };
        setRows(r);
        onPayloadChangeRef.current?.(payload);
      },
      []
    );

  // ===== load data (prefer props.fees; else fetch) - RUNS ONCE =====
  const feesKey = useMemo(() => JSON.stringify(fees || []), [fees]);
  const didLoadRef = React.useRef(false);

  React.useEffect(() => {
    // ✅ Prevent duplicate fetches
    if (!personId || didLoadRef.current) return;
    didLoadRef.current = true;
    
    let alive = true;

    async function fetchFees(): Promise<UiFee[]> {
      onBusyChangeRef.current?.(true);
      const res = await fetch(`${API}/special-fees/vendor/${supplierId}/by-user/${personId}`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) return [];
      const data = await res.json().catch(() => []);
      const filtered = (Array.isArray(data) ? data : []).filter((f: any) => f?.status !== false);

      // ✅ keep the full objects for the tax POST
      setRaw(filtered);
      onSourceFeesLoadedRef.current?.(filtered);
      
      const list: UiFee[] = filtered.map((f: any) => ({
        id: String(f.id || f._id || ""),
        title: String(f.name || ""),
        amount: Number(f.amount || 0),
        taxable: readResolveTaxable(f, ["taxable", "is_taxable"], true),
        required: !!f?.required,
        note: f?.note || "",
        status: f?.status,
      }));
      return list;
    }

    (async () => {
      setLoading(true);
      try {
        const list: UiFee[] = Array.isArray(fees) && fees.length ? fees : await fetchFees();
        // if (!alive) return;

        setUiFees(list);
        // console.log("listinside", list);

        // required ones must always be selected
        const requiredIds = list.filter((f) => f.required).map((f) => f.id);

        // Merge with current selection (if any) first
        let merged = Array.from(new Set([...(selected || []), ...requiredIds]));

        // Apply initialSelectedIds (ids or names) once list is loaded
        if (initialSelectedIds?.length) {
          const byId = new Set(list.map((f) => f.id));
          const byName = new Map(list.map((f) => [f.title.toLowerCase(), f.id]));
          for (const raw of initialSelectedIds) {
            const s = String(raw || "").trim();
            if (!s) continue;
            if (byId.has(s)) merged.push(s);
            else {
              const maybe = byName.get(s.toLowerCase());
              if (maybe) merged.push(maybe);
            }
          }
          merged = Array.from(new Set(merged.concat(requiredIds)));
        }

        setSelected(merged);
      } finally {
        if (alive) {
          setLoading(false);
          onBusyChangeRef.current?.(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [token, personId, feesKey,supplierId]);

 

  // Accept only known values; otherwise return empty string
const normalizeType = (x: any) => {
  const v = String(x ?? "").trim().toLowerCase();
  return v === "manual" || v === "taxcloud" || v === "geera" ? v : "";
};

// ✅ Cache to prevent repeated API calls
const taxTypeCache = React.useRef<{ value: string | null; fetched: boolean }>({ value: null, fetched: false });

// ✅ Minimal: call once, set state only if valid; NEVER coerce to manual
const ensureTaxType = React.useCallback(async (): Promise<string> => {
  // ✅ Return cached value if already fetched
  // if (taxTypeCache.current.fetched) {
  //   console.log("[TaxType] Using cached value:", taxTypeCache.current.value);
  //   return taxTypeCache.current.value || "";
  // }

  if (!token || !supplierId) {
    safeConsole.warn("[TaxType] missing token/supplierId → skip type detection");
    setGetStatusTaxType(undefined as any);
    taxTypeCache.current = { value: null, fetched: true };
    return "";
  }
  
  try {
    const res = await getActiveTaxType(token, supplierId);
    // console.log("[TaxType] API returned", res);
    const v = normalizeType(res);
    if (!v) {
      safeConsole.warn("[TaxType] API returned empty/unknown type → skip");
      setGetStatusTaxType(undefined as any);
      taxTypeCache.current = { value: null, fetched: true };
      return "";
    }
    setGetStatusTaxType(v);
    taxTypeCache.current = { value: v, fetched: true };
    return v;
  } catch (e) {
    safeConsole.warn("[TaxType] API failed → skip", e);
    setGetStatusTaxType(undefined as any);
    taxTypeCache.current = { value: null, fetched: true };
    return "";
  }
}, [token, supplierId]);

// ✅ Stable keys for dependencies to prevent unnecessary recalculations
const selectedKey = useMemo(() => JSON.stringify(selected.sort()), [selected]);
const addressKey = useMemo(() => JSON.stringify(shippingAddress), [shippingAddress]);
const computeEmitSeqRef = React.useRef(0);

const computeAndEmit = React.useCallback(
  async (ids: string[]) => {
    const emitSeq = ++computeEmitSeqRef.current;
    const isStale = () => emitSeq !== computeEmitSeqRef.current;

    const picked = uiFees.filter((f) => ids.includes(f.id));
    if (picked.length === 0) {
      if (!isStale()) emit([], []);
      return;
    }

    const emitZero = () => {
      if (isStale()) return;
      const rawById = new Map(raw.map((r: any) => [String(r.id || r._id || ""), r]));
      const zeroRows: SpecialFeeRow[] = picked.map((p) => ({
        specialfee_id: p.id,
        specialfee_name: p.title,
        specialfee_amount: Number(p.amount || 0),
        specialfee_tax_percentage: 0,
        specialfee_tax_amount: 0,
        specialfee_taxable: resolveTaxableFromSources(
          [rawById.get(p.id), p as Record<string, unknown>],
          ["taxable", "is_taxable", "specialfee_taxable"],
          true
        ),
        required: !!p.required,
      }));
      emit(zeroRows, ids);
    };

    // get live type; do NOT use any defaults
    const activeType = await ensureTaxType();
    if (isStale()) return;

    // only run manual calc when server explicitly says "manual"
    if (activeType !== "manual") {
      emitZero();
      return;
    }

    // manual preconditions
    const canTaxBasics = !!(token && shippingAddress);
    if (!canTaxBasics) { emitZero(); return; }

    try {
      onBusyChangeRef.current?.(true);

      const canTaxRuntime = !!(isTaxable && statusOk && shippingAddress);
      if (!canTaxRuntime) { emitZero(); return; }

      // prepare inputs (unchanged)
      const rawById = new Map(raw.map((r: any) => [String(r.id || r._id || ""), r]));
      const inputs = ids.map((id) => {
        const ui = uiFees.find((u) => u.id === id);
        return {
          fee: {
            ...(rawById.get(id) ?? {
              id,
              name: ui?.title ?? "",
              amount: Number(ui?.amount ?? 0),
            }),
            taxable: resolveTaxableFromSources(
              [rawById.get(id), ui as Record<string, unknown> | undefined],
              ["taxable", "is_taxable", "specialfee_taxable"],
              true
            ),
          },
          address: shippingAddress,
          createdById: personId,
          supplierId,
        };
      });

      // ✅ run manual only
      const result: any[] = await calcFeeTax(token, "manual" as any, inputs);
      if (isStale()) return;

      const normalized: SpecialFeeRow[] = (Array.isArray(result) ? result : []).map((r) => {
        const feeId = String(r.specialfee_id ?? "");
        const ui = uiFees.find((u) => u.id === feeId);
        return {
          specialfee_id: feeId,
          specialfee_name: String(r.specialfee_name ?? ""),
          specialfee_amount: Number(r.specialfee_amount || 0),
          specialfee_tax_percentage: Number(r.specialfee_tax_percentage || 0),
          specialfee_tax_amount: Number(r.specialfee_tax_amount || 0),
          specialfee_taxable: resolveTaxableFromSources(
            [rawById.get(feeId), r as Record<string, unknown>],
            ["specialfee_taxable", "taxable", "is_taxable"],
            true
          ),
          required: !!(rawById.get(feeId)?.required ?? ui?.required),
        };
      });

      emit(normalized, ids);
    } catch (e) {
      safeConsole.warn("[SpecialFee] manual calc failed → zero", e);
      emitZero();
    } finally {
      if (!isStale()) onBusyChangeRef.current?.(false);
    }
  },
  [
    uiFees,
    token,
    personId,
    supplierId,
    raw,
    ensureTaxType,
    emit,
    isTaxable,
    statusOk,
    addressKey, // ✅ Use stable key instead of object
  ]
);

// ✅ Use stable keys to prevent running on every render
React.useEffect(() => {
  computeAndEmit(selected);
}, [selectedKey, addressKey, computeAndEmit]);



// 1) Ensure we read the latest type (and include token in deps)
// const ensureTaxType = React.useCallback(async () => {
//   console.log("Ensuring tax type with token:", token, "and personId:", personId);
//   const res = await getActiveTaxType(token, personId);
//   setGetStatusTaxType(res);
//   return res; // e.g. "manual" | "taxcloud" | "geera" | ...
// }, [token, personId]);

// // 2) Compute + emit only calls calcFeeTax for manual
// const computeAndEmit = React.useCallback(
//   async (ids: string[]) => {
//     const picked = uiFees.filter((f) => ids.includes(f.id));
//     if (picked.length === 0) { emit([], []); return; }

//     const emitZero = () => {
//       const zeroRows: SpecialFeeRow[] = picked.map((p) => ({
//         specialfee_id: p.id,
//         specialfee_name: p.title,
//         specialfee_amount: asNum(p.amount),
//         specialfee_tax_percentage: 0,
//         specialfee_tax_amount: 0,
//         specialfee_taxable: p.taxable !== false,
//       }));
//       emit(zeroRows, ids);
//     };

//     // current cached type
//     console.log("Getting cached tax type:", getstatusTaxType);
//     const cachedType = String(getstatusTaxType ?? "").trim().toLowerCase();
//   console.log("Cached Tax Type:", cachedType);
//     // If cached is not manual, short-circuit immediately
//     if (cachedType !== "manual") { emitZero(); return; }

//     // Reconfirm live type (avoids stale closures / race conditions)
//     const liveType = String((await ensureTaxType()) ?? "").trim().toLowerCase();
//     if (liveType !== "manual") { emitZero(); return; } // 👈 absolute guard

//     // Minimal prerequisites in manual mode
//     const canTaxBasics = !!(token && shippingAddress);
//     console.log("Can Tax Basics:", canTaxBasics);
//     if (!canTaxBasics) { emitZero(); return; }
   
//     try {
//       onBusyChangeRef.current?.(true);

//       const canTaxRuntime = !!(isTaxable && statusOk && shippingAddress);
//       console.log("Can Tax Runtime:", canTaxRuntime);
//       if (!canTaxRuntime) { emitZero(); return; }

//       // Prepare inputs
//       const rawById = new Map(raw.map((r: any) => [String(r.id || r._id || ""), r]));
//       const inputs = ids.map((id) => ({
//         fee:
//           rawById.get(id) ?? {
//             id,
//             name: uiFees.find((u) => u.id === id)?.title ?? "",
//             amount: Number(uiFees.find((u) => u.id === id)?.amount ?? 0),
//             taxable: uiFees.find((u) => u.id === id)?.taxable !== false,
//           },
//         address: shippingAddress,
//         createdById: personId,
//         supplierId,
//       }));

//       // ✅ Will only execute if type is confirmed "manual"
//       const result: any[] = await calcFeeTax(token, "manual" as any, inputs);
//      console.log("Special Fee Tax Result:", result);
//       const normalized: SpecialFeeRow[] = (Array.isArray(result) ? result : []).map((r) => ({
//         specialfee_id: String(r.specialfee_id ?? ""),
//         specialfee_name: String(r.specialfee_name ?? ""),
//         specialfee_amount: asNum(r.specialfee_amount),
//         specialfee_tax_percentage: asNum(r.specialfee_tax_percentage),
//         specialfee_tax_amount: asNum(r.specialfee_tax_amount),
//         specialfee_taxable: r?.specialfee_taxable !== false,
//       }));

//       console.log("Special Fee Normalized:", normalized);
//       emit(normalized, ids);
//     } catch {
//       emitZero();
//     } finally {
//       onBusyChangeRef.current?.(false);
//     }
//   },
//   [
//     uiFees,
//     token,
//     shippingAddress,
//     personId,
//     supplierId,
//     raw,
//     ensureTaxType,
//     calcFeeTax,
//     emit,
//     getstatusTaxType,
//     isTaxable,
//     statusOk,
//   ]
// );

// unchanged

    // ===== expose ref API =====
    React.useImperativeHandle(
      ref,
      () => ({
        getPayload(): FeePayload {
          return {
            specialfee: rows,               // selected-only (already normalized)
            selected_fee_ids: selected,
            total_amount: rows.reduce((s, x) => s + asNum(x.specialfee_amount), 0),
            total_tax_amount: rows.reduce((s, x) => s + asNum(x.specialfee_tax_amount), 0),
            total_tax_percentage: 0,
          };
        },
        setSelectedIds(idsOrNames: string[]) {
          const ids = (idsOrNames || []).map(String);
          const byId = new Set(uiFees.map((f) => f.id));
          const byName = new Map(uiFees.map((f) => [f.title.toLowerCase(), f.id]));
          const final = new Set<string>();
          for (const raw of ids) {
            if (byId.has(raw)) final.add(raw);
            else {
              const maybe = byName.get(raw.toLowerCase());
              if (maybe) final.add(maybe);
            }
          }
          // keep required
          uiFees.filter((f) => f.required).forEach((f) => final.add(f.id));
          setSelected(Array.from(final));
        },
      }),
      [rows, selected, uiFees]
    );

    // After uiFees are loaded, (re)apply initialSelectedIds from parent.
// Works if ids are fee IDs or fee names.
React.useEffect(() => {
  if (!Array.isArray(initialSelectedIds) || initialSelectedIds.length === 0) return;
  if (!uiFees.length) return;

  const byId = new Set(uiFees.map((f) => f.id));
  const byName = new Map(uiFees.map((f) => [f.title.toLowerCase(), f.id]));

  const next = new Set<string>(selected); // keep anything already selected
  for (const raw of initialSelectedIds) {
    const s = String(raw || "").trim();
    if (!s) continue;
    if (byId.has(s)) {
      next.add(s);
    } else {
      const maybe = byName.get(s.toLowerCase());
      if (maybe) next.add(maybe);
    }
  }

  // always keep required
  uiFees.filter((f) => f.required).forEach((f) => next.add(f.id));

  const nextArr = Array.from(next);
  // only set if changed (avoids re-renders)
  if (nextArr.sort().join(",") !== [...selected].sort().join(",")) {
    setSelected(nextArr);
  }
}, [
  // re-run when the list is ready or IDs change
  uiFees.length,
  JSON.stringify(initialSelectedIds || []),
]);


    // ===== UI (card tiles, like shipping) =====
    return (
      <div className={className}>
        <div className="card shadow-border-1">
          <div className="card-header bg-light">
            <h5 className="align-items-center card-title d-flex mb-0">
              <AppIcon name="dollar3" size={22} alt="Special Fees" className="me-2" />
              <span>Special Fees</span>
            </h5>
          </div>

          <div className="card-body">
            {/* {loading && <div className="text-muted small mb-2">loading…</div>} */}
            {!loading && uiFees.length === 0 && <div className="text-muted small">No special fees</div>}

            {uiFees.map((f) => {
              const checked = selected.includes(f.id);
              return (
                <div
                  key={f.id}
                  className={
                    `d-flex justify-content-between align-items-center p-3 mb-2 border rounded-4 cursor-pointer ` +
                    (checked ? "border-primary bg-primary-subtle" : "border-light hover-border-primary")
                  }
                  onClick={() => {
                    if (f.required) return;                 // lock required
                    setSelected((prev) => {
                      const on = !prev.includes(f.id);
                      return on ? Array.from(new Set([...prev, f.id])) : prev.filter((x) => x !== f.id);
                    });
                  }}
                  style={{ userSelect: "none" }}
                >
                  <div className="d-flex align-items-start gap-3">
                    <input
                      type="checkbox"
                      className="form-check-input mt-1"
                      checked={checked}
                      disabled={f.required}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setSelected((prev) => {
                          return on ? Array.from(new Set([...prev, f.id])) : prev.filter((x) => x !== f.id);
                        });
                      }}
                    />
                    <div>
                      <div className="fw-semibold">
                        {f.title}{" "}
                        {f.required && <span className="text-danger ms-1 ">*</span>}
                      </div>
                      {/* <div className="text-muted small">{f.taxable ? "Taxable" : "Non-taxable"}</div> */}
                      {f.note && <div className="small mt-1">{f.note}</div>}
                    </div>
                  </div>

                  <div className="fw-semibold text-nowrap">{money(f.amount)}</div>
                </div>
              );
            })}

            {/* {rows.length > 0 && (
              <div className="mt-2 small">
                {rows.map((r) => (
                  <div key={r.specialfee_id} className="d-flex justify-content-between">
                    <span>{r.specialfee_name}</span>
                    <span>
                      {money(r.specialfee_amount)}{" "}
                      <span className="text-muted">
                        (+{money(r.specialfee_tax_amount)} / {asNum(r.specialfee_tax_percentage).toFixed(2)}%)
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )} */}
          </div>
        </div>
      </div>
    );
  }
);

export default SpecialFeePicker;

// // SpecialFeeui.tsx
// import React from "react";
// import {
//   getActiveTaxType,
//   calcSpecialFeeTax as calspecicalfee,
//   type SpecialFeeCalcResult,
//   type SpecialFeeInput,
// } from "components/Tax/manualtax";

// /** === Types exposed to parent ==== */
// export type SpecialFeeRow = {
//   specialfee_id: string;
//   specialfee_name: string;
//   specialfee_amount: number;
//   specialfee_tax_percentage: number;
//   specialfee_tax_amount: number;
//   specialfee_taxable: boolean;
// };

// export type FeePayload = {
//   specialfee: SpecialFeeRow[];
//   selected_fee_ids: string[];
//   total_amount: number;
//   total_tax_amount: number;
//   total_tax_percentage: number; // optional aggregate
// };

// export type SpecialFeePickerV2Handle = {
//   /** current payload (what you’ll send to order create/update) */
//   getPayload(): FeePayload;
//   /** programmatically update selected fee ids (ids or names OK) */
//   setSelectedIds(ids: string[]): void;
// };

// /** ==== Internal UI types ==== */
// type UiFee = {
//   id: string;
//   title: string;
//   amount: number;
//   taxable: boolean;
//   required: boolean;
//   note?: string;
//   status?: boolean;
// };

// function mapToUi(f: {
//   id: string;
//   name: string;
//   amount: number;
//   taxable?: boolean;
//   required?: boolean;
//   note?: string;
//   status?: boolean;
// }): UiFee {
//   return {
//     id: String(f.id || ""),
//     title: String(f.name || ""),
//     amount: Number(f.amount || 0),
//     taxable: f?.taxable !== false,
//     required: !!f?.required,
//     note: f?.note || "",
//     status: f?.status,
//   };
// }

// function money(n: any) {
//   const v = Number(n || 0);
//   return `$${v.toFixed(2)}`;
// }

// /** ==== Props from parent ==== */
// type Props = {
//   token: string;
//   API?: string;

//   /** created_by / supplier id used by /created-by/:id endpoints */
//   personId: string;

//   /** currently selected shipping address (full object) */
//   shippingAddress: any | null;

//   /** Optional: if you already know tax type, pass it to skip fetch */
//   knownTaxType?: "manual" | "taxcloud" | "geera";

//   /** configuration */
//   isTaxable?: boolean;
//   statusOk?: boolean;
//   persistKey?: string;
//   className?: string;

//   /** optional callbacks */
//   onChange?: (selectedIds: string[], amountSum: number) => void;
//   onPayloadChange?: (payload: FeePayload) => void;

//   /** Preselect coming from Edit’s existing order (ids or names) */
//   initialSelectedIds?: string[] | null;
// };

// /** ==== Component (forwardRef) ==== */
// const SpecialFeePicker = React.forwardRef<SpecialFeePickerV2Handle, Props>(function SpecialFeePicker(
//   {
//     token,
//     API = process.env.REACT_APP_API_URL || "",
//     personId,
//     shippingAddress,
//     knownTaxType,
//     isTaxable = true,
//     statusOk = true,
//     persistKey = "specialfee:selected",
//     className = "",
//     onChange,
//     onPayloadChange,
//     initialSelectedIds,
//   },
//   ref
// ) {
//   const [loading, setLoading] = React.useState(false);

//   // full objects from server (we keep these to send entire fee objects to backend)
//   const [raw, setRaw] = React.useState<any[]>([]);
//   // simplified for UI
//   const [uiFees, setUiFees] = React.useState<UiFee[]>([]);
//   console.log("uiFees", uiFees)
//   // controlled selection
//   const [selected, setSelected] = React.useState<string[]>([]);
//   // computed rows (with tax)
//   const [rows, setRows] = React.useState<SpecialFeeRow[]>([]);

//   /** Tax type resolver */
//   const ensureTaxType = React.useCallback(async () => {
//     if (knownTaxType) return knownTaxType;
//     return getActiveTaxType(token,personId);
//   }, [knownTaxType, personId]);

//   /** Load fees for this user (required + optional) */
//   React.useEffect(() => {
//     let alive = true;
//     (async () => {
//       if (!token || !personId) {
//         setRaw([]);
//         setUiFees([]);
//         return;
//       }
//       setLoading(true);
//       try {
//         const res = await fetch(`${API}/special-fees/by-user/${personId}`, {
//           headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//         });
//         let data: any[] = [];
//         if (res.ok) data = await res.json().catch(() => []);
//         const filtered = (data || []).filter((f: any) => f?.status !== false);

//         const mapped = filtered.map((f: any) =>
//           mapToUi({
//             id: String(f.id || f._id || ""),
//             name: f.name,
//             amount: Number(f.amount || 0),
//             taxable: f?.taxable !== false,
//             required: !!f?.required,
//             note: f?.note || "",
//             status: f?.status,
//           })
//         );

//         const requiredIds = mapped.filter((f) => f.required).map((f) => f.id);
//         // merge with current selection (we’ll also apply initialSelectedIds below)
//         const merged = Array.from(new Set([...(selected || []), ...requiredIds]));

//         if (!alive) return;
//         setRaw(filtered);
//         setUiFees(mapped);
//         setSelected(merged);

//         // tell parent immediate amount (pre-tax)
//         const total = mapped
//           .filter((f) => merged.includes(f.id))
//           .reduce((n, f) => n + Number(f.amount || 0), 0);
//         onChange?.(merged, total);
//       } finally {
//         if (alive) setLoading(false);
//       }
//     })();
//     return () => {
//       alive = false;
//     };
//   }, [token, personId]);

//   /** Preselect from Edit (ids or names) once list is ready */
//   React.useEffect(() => {
//     if (!initialSelectedIds || !uiFees.length) return;

//     const byId = new Set(uiFees.map((f) => f.id));
//     const byName = new Map(uiFees.map((f) => [f.title.toLowerCase(), f.id]));

//     const finalIds = new Set<string>();
//     for (const x of initialSelectedIds) {
//       const s = String(x || "").trim();
//       if (!s) continue;
//       if (byId.has(s)) {
//         finalIds.add(s);
//         continue;
//       }
//       const maybe = byName.get(s.toLowerCase());
//       if (maybe) finalIds.add(maybe);
//     }

//     if (finalIds.size) {
//       // keep required too
//       const requiredIds = uiFees.filter((f) => f.required).map((f) => f.id);
//       setSelected(Array.from(new Set([...Array.from(finalIds), ...requiredIds])));
//     }
//   }, [initialSelectedIds, uiFees]);

//   /** Compute taxes whenever selection OR shipping address changes */
//   const computeRows = React.useCallback(
//     async (ids: string[]) => {
//       const picked = uiFees.filter((f) => ids.includes(f.id));
//       if (picked.length === 0) {
//         setRows([]);
//         onPayloadChange?.({
//           specialfee: [],
//           selected_fee_ids: [],
//           total_amount: 0,
//           total_tax_amount: 0,
//           total_tax_percentage: 0,
//         });
//         return;
//       }

//       const tt = await ensureTaxType();
//       const canTax = !!(isTaxable && statusOk && shippingAddress);

//       // map selected ids -> FULL raw objects (what tax API needs)
//       const rawById = new Map(raw.map((r) => [String(r.id || r._id || ""), r]));
//       const inputs: SpecialFeeInput[] = picked.map((p) => {
//         const full = rawById.get(p.id) || { id: p.id, name: p.title, amount: p.amount, taxable: p.taxable };
//         return {
//           fee: full,
//           address: shippingAddress,
//           createdById: personId,
//         };
//       });

//       // cannot tax → zero-tax rows
//       if (!canTax) {
//         const zeroRows: SpecialFeeRow[] = inputs.map(({ fee }) => ({
//           specialfee_id: String(fee.id || fee._id || ""),
//           specialfee_name: String(fee.name || ""),
//           specialfee_amount: Number(fee.amount || 0),
//           specialfee_tax_percentage: 0,
//           specialfee_tax_amount: 0,
//           specialfee_taxable: fee?.taxable !== false,
//         }));
//         setRows(zeroRows);
//         onPayloadChange?.({
//           specialfee: zeroRows,
//           selected_fee_ids: ids,
//           total_amount: zeroRows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//           total_tax_amount: 0,
//           total_tax_percentage: 0,
//         });
//         return;
//       }

//       try {
//         const result: SpecialFeeCalcResult[] = await calspecicalfee(token, tt as any, inputs);
//         const normalized: SpecialFeeRow[] = (Array.isArray(result) ? result : []).map((r) => ({
//           specialfee_id: String(r.specialfee_id ?? ""),
//           specialfee_name: String(r.specialfee_name ?? ""),
//           specialfee_amount: Number(r.specialfee_amount ?? 0),
//           specialfee_tax_percentage: Number(r.specialfee_tax_percentage ?? 0),
//           specialfee_tax_amount: Number(r.specialfee_tax_amount ?? 0),
//           specialfee_taxable: r?.specialfee_taxable !== false,
//         }));

//         setRows(normalized);
//         onPayloadChange?.({
//           specialfee: normalized,
//           selected_fee_ids: ids,
//           total_amount: normalized.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//           total_tax_amount: normalized.reduce((s, r) => s + Number(r.specialfee_tax_amount || 0), 0),
//           total_tax_percentage: 0,
//         });
//       } catch {
//         // fallback zero-tax
//         const zeroRows: SpecialFeeRow[] = inputs.map(({ fee }) => ({
//           specialfee_id: String(fee.id || fee._id || ""),
//           specialfee_name: String(fee.name || ""),
//           specialfee_amount: Number(fee.amount || 0),
//           specialfee_tax_percentage: 0,
//           specialfee_tax_amount: 0,
//           specialfee_taxable: fee?.taxable !== false,
//         }));
//         setRows(zeroRows);
//         onPayloadChange?.({
//           specialfee: zeroRows,
//           selected_fee_ids: ids,
//           total_amount: zeroRows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//           total_tax_amount: 0,
//           total_tax_percentage: 0,
//         });
//       }
//     },
//     [uiFees, raw, isTaxable, statusOk, shippingAddress, ensureTaxType, token, personId, onPayloadChange]
//   );

//   React.useEffect(() => {
//     computeRows(selected);
//   }, [selected, shippingAddress, computeRows]);

//   /** Expose ref handle to parent */
//   React.useImperativeHandle(
//     ref,
//     () => ({
//       getPayload(): FeePayload {
//         const payload: FeePayload = {
//           specialfee: rows,
//           selected_fee_ids: selected,
//           total_amount: rows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//           total_tax_amount: rows.reduce((s, r) => s + Number(r.specialfee_tax_amount || 0), 0),
//           total_tax_percentage: 0,
//         };
//         return payload;
//       },
//       setSelectedIds(ids: string[]) {
//         // ids or names accepted
//         const idsOrNames = Array.from(new Set((ids || []).map(String)));
//         const byId = new Set(uiFees.map((f) => f.id));
//         const byName = new Map(uiFees.map((f) => [f.title.toLowerCase(), f.id]));
//         const finalIds = new Set<string>();
//         for (const x of idsOrNames) {
//           if (byId.has(x)) { finalIds.add(x); continue; }
//           const maybe = byName.get(x.toLowerCase());
//           if (maybe) finalIds.add(maybe);
//         }
//         const requiredIds = uiFees.filter((f) => f.required).map((f) => f.id);
//         setSelected(Array.from(new Set([...Array.from(finalIds), ...requiredIds])));
//       },
//     }),
//     [rows, selected, uiFees]
//   );
 
//   /** UI — card tiles like Shipping */
//   return (
//     <div className={className}>
//       <div className="card shadow-border-3 box-shadow">
//         <div className="card-header bg-light">
//           <h5 className="align-items-center card-title d-flex mb-0">
//             <i className="ri-wallet-3-line align-bottom me-2 fs-2" style={{ color: "#2563eb" }} />
//             Special Fees
//           </h5>
//         </div>

//         <div className="card-body">
//           {loading && <div className="text-muted small mb-2">loading…</div>}
//           {uiFees.length === 0 && <div className="text-muted small">No special fees</div>}

//           {uiFees.map((f) => {
//             const checked = selected.includes(f.id);
//             return (
//               <div
//                 key={f.id}
//                 className={
//                   `d-flex justify-content-between align-items-center p-3 mb-2 border rounded-4 cursor-pointer ` +
//                   (checked ? "border-primary bg-primary-subtle" : "border-light hover-border-primary")
//                 }
//                 onClick={() => {
//                   if (f.required) return;
//                   setSelected((prev) => {
//                     const on = !prev.includes(f.id);
//                     const next = on ? Array.from(new Set([...prev, f.id])) : prev.filter((x) => x !== f.id);
//                     const amountSum = uiFees
//                       .filter((u) => next.includes(u.id))
//                       .reduce((s, u) => s + Number(u.amount || 0), 0);
//                     onChange?.(next, amountSum);
//                     return next;
//                   });
//                 }}
//                 style={{ userSelect: "none" }}
//               >
//                 <div className="d-flex align-items-start gap-3">
//                   <input
//                     type="checkbox"
//                     className="form-check-input mt-1"
//                     checked={checked}
//                     disabled={f.required}
//                     onClick={(e) => e.stopPropagation()}
//                     onChange={(e) => {
//                       const on = e.target.checked;
//                       setSelected((prev) => {
//                         const next = on ? Array.from(new Set([...prev, f.id])) : prev.filter((x) => x !== f.id);
//                         const amountSum = uiFees
//                           .filter((u) => next.includes(u.id))
//                           .reduce((s, u) => s + Number(u.amount || 0), 0);
//                         onChange?.(next, amountSum);
//                         return next;
//                       });
//                     }}
//                   />
//                   <div>
//                     <div className="fw-semibold">
//                       {f.title}{" "}
//                       {f.required && <span className="badge bg-secondary-subtle text-secondary ms-1">Required</span>}
//                     </div>
//                     <div className="text-muted small">{f.taxable ? "Taxable" : "Non-taxable"}</div>
//                     {f.note && <div className="small mt-1">{f.note}</div>}
//                   </div>
//                 </div>

//                 <div className="fw-semibold text-nowrap">{money(f.amount)}</div>
//               </div>
//             );
//           })}

//           {rows.length > 0 && (
//             <div className="mt-2 small">
//               {rows.map((r) => (
//                 <div key={r.specialfee_id} className="d-flex justify-content-between">
//                   <span>{r.specialfee_name}</span>
//                   <span>
//                     {money(r.specialfee_amount)}{" "}
//                     <span className="text-muted">
//                       (+{money(r.specialfee_tax_amount)} / {Number(r.specialfee_tax_percentage || 0).toFixed(2)}%)
//                     </span>
//                   </span>
//                 </div>
//               ))}
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// });

// export default SpecialFeePicker;

















// // SpecialFeeui.tsx
// import React from "react";
// import {
//   getActiveTaxType,
//   calcSpecialFeeTax as calspecicalfee,
//   type SpecialFeeCalcResult,
//   type SpecialFeeInput,
// } from "components/Tax/manualtax";

// /** === Types exposed to parent ==== */
// export type SpecialFeeRow = {
//   specialfee_id: string;
//   specialfee_name: string;
//   specialfee_amount: number;
//   specialfee_tax_percentage: number;
//   specialfee_tax_amount: number;
//   specialfee_taxable: boolean;
// };

// export type FeePayload = {
//   specialfee: SpecialFeeRow[];
//   selected_fee_ids: string[];
//   total_amount: number;
//   total_tax_amount: number;
//   total_tax_percentage: number; // optional aggregate
// };

// export type SpecialFeePickerV2Handle = {
//   /** current payload (what you’ll send to order create/update) */
//   getPayload(): FeePayload;
//   /** programmatically update selected fee ids */
//   setSelectedIds(ids: string[]): void;
// };

// /** ==== Internal UI types ==== */
// type UiFee = {
//   id: string;
//   title: string;
//   amount: number;
//   taxable: boolean;
//   required: boolean;
//   note?: string;
//   status?: boolean;
// };

// function mapToUi(f: {
//   id: string;
//   name: string;
//   amount: number;
//   taxable?: boolean;
//   required?: boolean;
//   note?: string;
//   status?: boolean;
// }): UiFee {
//   return {
//     id: String(f.id),
//     title: String(f.name || ""),
//     amount: Number(f.amount || 0),
//     taxable: f?.taxable !== false,
//     required: !!f?.required,
//     note: f?.note || "",
//     status: f?.status,
//   };
// }

// function money(n: any) {
//   const v = Number(n || 0);
//   return `$${v.toFixed(2)}`;
// }

// /** ==== Props from parent ==== */
// type Props = {
//   token: string;
//   API?: string;

//   /** created_by / supplier id used by /created-by/:id endpoints */
//   personId: string;

//   /** currently selected shipping address (full object) */
//   shippingAddress: any | null;

//   /** Optional: if you already know tax type, pass it to skip fetch */
//   knownTaxType?: "manual" | "taxcloud" | "geera";

//   /** configuration */
//   isTaxable?: boolean;
//   statusOk?: boolean;
//   persistKey?: string;
//   className?: string;

//   /** optional callbacks */
//   onChange?: (selectedIds: string[], amountSum: number) => void;
//   onPayloadChange?: (payload: FeePayload) => void;
// };

// /** ==== Component (forwardRef) ==== */
// const SpecialFeePicker = React.forwardRef<SpecialFeePickerV2Handle, Props>(function SpecialFeePicker(
//   {
//     token,
//     API = process.env.REACT_APP_API_URL || "",
//     personId,
//     shippingAddress,
//     knownTaxType,
//     isTaxable = true,
//     statusOk = true,
//     persistKey = "specialfee:selected",
//     className = "",
//     onChange,
//     onPayloadChange,
//   },
//   ref
// ) {
//   const [loading, setLoading] = React.useState(false);

//   // full objects from server (we keep these to send entire fee objects to backend)
//   const [raw, setRaw] = React.useState<any[]>([]);
//   // simplified for UI
//   const [uiFees, setUiFees] = React.useState<UiFee[]>([]);
//   // controlled selection (from localStorage + required)
//   const [selected, setSelected] = React.useState<string[]>(() => {
//     try {
//       const saved = JSON.parse(localStorage.getItem(persistKey) || "[]");
//       return Array.isArray(saved) ? saved.map(String) : [];
//     } catch {
//       return [];
//     }
//   });
//   // rows shown (computed tax rows)
//   const [rows, setRows] = React.useState<SpecialFeeRow[]>([]);

//   /** Tax type resolver */
//   const ensureTaxType = React.useCallback(async () => {
//     if (knownTaxType) return knownTaxType;
//     return getActiveTaxType( personId);
//   }, [knownTaxType, token, personId]);

//   /** Persist selection */
//   // React.useEffect(() => {
//   //   localStorage.setItem(persistKey, JSON.stringify(selected));
//   // }, [persistKey, selected]);

//   /** Load fees for this user (required + optional) */
//   React.useEffect(() => {
//     let alive = true;
//     (async () => {
//       if (!token || !personId) {
//         setRaw([]);
//         setUiFees([]);
//         return;
//       }
//       setLoading(true);
//       try {
//         const res = await fetch(`${API}/special-fees/by-user/${personId}`, {
//           headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//         });
//         let data: any[] = [];
//         if (res.ok) data = await res.json().catch(() => []);
//         const filtered = (data || []).filter((f: any) => f?.status !== false);

//         const mapped = filtered.map((f: any) =>
//           mapToUi({
//             id: String(f.id || f._id || ""),
//             name: f.name,
//             amount: Number(f.amount || 0),
//             taxable: f?.taxable !== false,
//             required: !!f?.required,
//             note: f?.note || "",
//             status: f?.status,
//           })
//         );

//         // auto-select "required" fees (merge with previous selection)
//         const requiredIds = mapped.filter((f) => f.required).map((f) => f.id);
//         const merged = Array.from(new Set([...(selected || []), ...requiredIds]));

//         if (!alive) return;
//         setRaw(filtered);
//         setUiFees(mapped);
//         setSelected(merged);

//         // notify parent about selected + amount sum
//         const total = mapped
//           .filter((f) => merged.includes(f.id))
//           .reduce((n, f) => n + Number(f.amount || 0), 0);
//         onChange?.(merged, total);
//       } finally {
//         if (alive) setLoading(false);
//       }
//     })();
//     return () => {
//       alive = false;
//     };
//   }, [token, personId]);

//   /** Compute taxes whenever selection OR shipping address changes */
//   // const computeRows = React.useCallback(
//   //   async (ids: string[]) => {
//   //     const picked = uiFees.filter((f) => ids.includes(f.id));
//   //     if (picked.length === 0) {
//   //       setRows([]);
//   //       onPayloadChange?.({
//   //         specialfee: [],
//   //         selected_fee_ids: [],
//   //         total_amount: 0,
//   //         total_tax_amount: 0,
//   //         total_tax_percentage: 0,
//   //       });
//   //       return;
//   //     }

//   //     const tt = await ensureTaxType();
//   //     const canTax = !!(isTaxable && statusOk && shippingAddress);

//   //     // If we cannot compute tax (no address or disabled), return zero-tax rows from UI amounts
//   //     if (!canTax) {
//   //       const zeroRows: SpecialFeeRow[] = picked.map((p) => ({
//   //         specialfee_id: p.id,
//   //         specialfee_name: p.title,
//   //         specialfee_amount: Number(p.amount || 0),
//   //         specialfee_tax_percentage: 0,
//   //         specialfee_tax_amount: 0,
//   //         specialfee_taxable: p.taxable !== false,
//   //       }));
//   //       setRows(zeroRows);
//   //       onPayloadChange?.({
//   //         specialfee: zeroRows,
//   //         selected_fee_ids: ids,
//   //         total_amount: zeroRows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//   //         total_tax_amount: 0,
//   //         total_tax_percentage: 0,
//   //       });
//   //       return;
//   //     }

//   //     // Build inputs from FULL RAW OBJECTS (send entire objects to backend)
//   //     const rawById = new Map(raw.map((r) => [String(r.id || r._id || ""), r]));
//   //     const inputs: SpecialFeeInput[] = picked.map((p) => {
//   //       const full = rawById.get(p.id) || {
//   //         id: p.id,
//   //         name: p.title,
//   //         amount: p.amount,
//   //         taxable: p.taxable,
//   //       };
//   //       return {
//   //         fee: full,
//   //         address: shippingAddress,
//   //         createdById: personId,
//   //       };
//   //     });

//   //     try {
//   //       const result: SpecialFeeCalcResult[] = await calspecicalfee(token, tt as any, inputs);

//   //       const normalized: SpecialFeeRow[] = (Array.isArray(result) ? result : []).map((r) => ({
//   //         specialfee_id: String(r.specialfee_id ?? ""),
//   //         specialfee_name: String(r.specialfee_name ?? ""),
//   //         specialfee_amount: Number(r.specialfee_amount ?? 0),
//   //         specialfee_tax_percentage: Number(r.specialfee_tax_percentage ?? 0),
//   //         specialfee_tax_amount: Number(r.specialfee_tax_amount ?? 0),
//   //         specialfee_taxable: r?.specialfee_taxable !== false,
//   //       }));

//   //       setRows(normalized);
//   //       onPayloadChange?.({
//   //         specialfee: normalized,
//   //         selected_fee_ids: ids,
//   //         total_amount: normalized.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//   //         total_tax_amount: normalized.reduce((s, r) => s + Number(r.specialfee_tax_amount || 0), 0),
//   //         total_tax_percentage: 0,
//   //       });
//   //     } catch (e) {
//   //       // fallback to zero-tax if helper fails
//   //       const zeroRows: SpecialFeeRow[] = picked.map((p) => ({
//   //         specialfee_id: p.id,
//   //         specialfee_name: p.title,
//   //         specialfee_amount: Number(p.amount || 0),
//   //         specialfee_tax_percentage: 0,
//   //         specialfee_tax_amount: 0,
//   //         specialfee_taxable: p.taxable !== false,
//   //       }));
//   //       setRows(zeroRows);
//   //       onPayloadChange?.({
//   //         specialfee: zeroRows,
//   //         selected_fee_ids: ids,
//   //         total_amount: zeroRows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//   //         total_tax_amount: 0,
//   //         total_tax_percentage: 0,
//   //       });
//   //     }
//   //   },
//   //   [uiFees, raw, isTaxable, statusOk, shippingAddress, ensureTaxType, token, personId, onPayloadChange]
//   // );

//   // inside SpecialFeeui.tsx

// const computeRows = React.useCallback(
//   async (ids: string[]) => {
//     const picked = uiFees.filter(f => ids.includes(f.id));
//     if (picked.length === 0) {
//       setRows([]);
//       onPayloadChange?.({
//         specialfee: [],
//         selected_fee_ids: [],
//         total_amount: 0,
//         total_tax_amount: 0,
//         total_tax_percentage: 0,
//       });
//       return;
//     }

//     // get active type (manual/taxcloud/geera)
//     const tt = await ensureTaxType();
//     const canTax = !!(isTaxable && statusOk && shippingAddress);

//     // map selected ids back to the FULL raw fee objects (exactly as the API expects)
//     const rawById = new Map(raw.map(r => [String(r.id || r._id || ""), r]));
//     const inputs: SpecialFeeInput[] = picked.map(p => ({
//       fee: rawById.get(p.id) ?? {
//         id: p.id, name: p.title, amount: p.amount, taxable: p.taxable,
//       },
//       address: shippingAddress,
//       createdById: personId,
//     }));

//     // when we can't calculate taxes (e.g. missing address), return zero-tax rows
//     if (!canTax) {
//       const zeroRows: SpecialFeeRow[] = inputs.map(({ fee }) => ({
//         specialfee_id: String(fee.id || fee._id || ""),
//         specialfee_name: String(fee.name || ""),
//         specialfee_amount: Number(fee.amount || 0),
//         specialfee_tax_percentage: 0,
//         specialfee_tax_amount: 0,
//         specialfee_taxable: fee?.taxable !== false,
//       }));
//       setRows(zeroRows);
//       onPayloadChange?.({
//         specialfee: zeroRows,
//         selected_fee_ids: ids,
//         total_amount: zeroRows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//         total_tax_amount: 0,
//         total_tax_percentage: 0,
//       });
//       return;
//     }

//     try {
//       // 🔑 calls the function you updated in manualtax.tsx
//       const result = await calspecicalfee(token, tt as any, inputs);

//       const normalized: SpecialFeeRow[] = (Array.isArray(result) ? result : []).map(r => ({
//         specialfee_id: String(r.specialfee_id ?? ""),
//         specialfee_name: String(r.specialfee_name ?? ""),
//         specialfee_amount: Number(r.specialfee_amount ?? 0),
//         specialfee_tax_percentage: Number(r.specialfee_tax_percentage ?? 0),
//         specialfee_tax_amount: Number(r.specialfee_tax_amount ?? 0),
//         specialfee_taxable: r?.specialfee_taxable !== false,
//       }));

//       setRows(normalized);
//       onPayloadChange?.({
//         specialfee: normalized,
//         selected_fee_ids: ids,
//         total_amount: normalized.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//         total_tax_amount: normalized.reduce((s, r) => s + Number(r.specialfee_tax_amount || 0), 0),
//         total_tax_percentage: 0, // optional aggregate if you want it
//       });
//     } catch {
//       // fallback to zero-tax on error
//       const zeroRows: SpecialFeeRow[] = inputs.map(({ fee }) => ({
//         specialfee_id: String(fee.id || fee._id || ""),
//         specialfee_name: String(fee.name || ""),
//         specialfee_amount: Number(fee.amount || 0),
//         specialfee_tax_percentage: 0,
//         specialfee_tax_amount: 0,
//         specialfee_taxable: fee?.taxable !== false,
//       }));
//       setRows(zeroRows);
//       onPayloadChange?.({
//         specialfee: zeroRows,
//         selected_fee_ids: ids,
//         total_amount: zeroRows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//         total_tax_amount: 0,
//         total_tax_percentage: 0,
//       });
//     }
//   },
//   [uiFees, raw, isTaxable, statusOk, shippingAddress, ensureTaxType, token, personId, onPayloadChange]
// );

//   /** Recompute when selection or address changes */
//   React.useEffect(() => {
//     computeRows(selected);
//   }, [selected, shippingAddress, computeRows]);

//   /** Expose ref handle to parent */
//   React.useImperativeHandle(ref, () => ({
//     getPayload(): FeePayload {
//       const payload: FeePayload = {
//         specialfee: rows,
//         selected_fee_ids: selected,
//         total_amount: rows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//         total_tax_amount: rows.reduce((s, r) => s + Number(r.specialfee_tax_amount || 0), 0),
//         total_tax_percentage: 0,
//       };
//       return payload;
//     },
//     setSelectedIds(ids: string[]) {
//       setSelected(Array.from(new Set((ids || []).map(String))));
//     },
//   }), [rows, selected]);
  

//   /** UI */
//     return (
//       <div className={className}>
//         {uiFees.length > 0 && (
//           <div className="card shadow-border-3 box-shadow">
//             <div className="card-header bg-light">
//               <h5 className="align-items-center card-title d-flex mb-0">
//                 <i className="ri-truck-line align-bottom me-2 fs-2" style={{ color: "#2563eb" }} />
//                 Special Fee
//               </h5>
//             </div>
//             <div className="card-body">
//              {uiFees.map((f) => {
//         const checked = selected.includes(f.id);
//         return (
//           <label key={f.id} className="d-flex justify-content-between align-items-center py-1">
//             <span className="d-flex align-items-center gap-2">
//               <input
//                 type="checkbox"
//                 checked={checked}
//                 disabled={f.required}
//                 onChange={(e) => {
//                   const on = e.target.checked;
//                   setSelected((prev) => {
//                     const next = on ? Array.from(new Set([...prev, f.id])) : prev.filter((x) => x !== f.id);
//                     const amountSum = uiFees
//                       .filter((u) => next.includes(u.id))
//                       .reduce((s, u) => s + Number(u.amount || 0), 0);
//                     onChange?.(next, amountSum);
//                     return next;
//                   });
//                 }}
//               />
//               <span>{f.title}</span>
//             </span>
//             <span className="text-nowrap">{money(f.amount)}</span>
//           </label>
//         );
//       })}
//             </div>
//           </div>
//         )}
//       </div>
//     );
//   // return (
//   //   <div className={className}>
//   //     <div className="d-flex align-items-center justify-content-between mb-2">
//   //       <h6 className="mb-0">Special Fees</h6>
//   //       {loading && <small className="text-muted">loading…</small>}
//   //     </div>

//   //     {uiFees.length === 0 && <div className="text-muted small">No special fees</div>}

//   //     {uiFees.map((f) => {
//   //       const checked = selected.includes(f.id);
//   //       return (
//   //         <label key={f.id} className="d-flex justify-content-between align-items-center py-1">
//   //           <span className="d-flex align-items-center gap-2">
//   //             <input
//   //               type="checkbox"
//   //               checked={checked}
//   //               disabled={f.required}
//   //               onChange={(e) => {
//   //                 const on = e.target.checked;
//   //                 setSelected((prev) => {
//   //                   const next = on ? Array.from(new Set([...prev, f.id])) : prev.filter((x) => x !== f.id);
//   //                   const amountSum = uiFees
//   //                     .filter((u) => next.includes(u.id))
//   //                     .reduce((s, u) => s + Number(u.amount || 0), 0);
//   //                   onChange?.(next, amountSum);
//   //                   return next;
//   //                 });
//   //               }}
//   //             />
//   //             <span>{f.title}</span>
//   //           </span>
//   //           <span className="text-nowrap">{money(f.amount)}</span>
//   //         </label>
//   //       );
//   //     })}

//   //     {rows.length > 0 && (
//   //       <div className="mt-2 small">
//   //         {rows.map((r) => (
//   //           <div key={r.specialfee_id} className="d-flex justify-content-between">
//   //             <span>{r.specialfee_name}</span>
//   //             <span>
//   //               {money(r.specialfee_amount)}{" "}
//   //               <span className="text-muted">
//   //                 (+{money(r.specialfee_tax_amount)} / {Number(r.specialfee_tax_percentage || 0).toFixed(2)}%)
//   //               </span>
//   //             </span>
//   //           </div>
//   //         ))}
//   //       </div>
//   //     )}
//   //   </div>
//   // );
// });

// export default SpecialFeePicker;




// SpecialFeeui.tsx
// import React from "react";
// import {
//   getActiveTaxType,
//   calcSpecialFeeTax as calspecicalfee,
//   type SpecialFeeCalcResult,
//   type SpecialFeeInput,
// } from "components/Tax/manualtax";

// /** === Types exposed to parent ==== */
// export type SpecialFeeRow = {
//   specialfee_id: string;
//   specialfee_name: string;
//   specialfee_amount: number;
//   specialfee_tax_percentage: number;
//   specialfee_tax_amount: number;
//   specialfee_taxable: boolean;
// };

// export type FeePayload = {
//   specialfee: SpecialFeeRow[];
//   selected_fee_ids: string[];
//   total_amount: number;
//   total_tax_amount: number;
//   total_tax_percentage: number; // optional aggregate
// };

// export type SpecialFeePickerV2Handle = {
//   /** current payload (what you’ll send to order create/update) */
//   getPayload(): FeePayload;
//   /** programmatically update selected fee ids */
//   setSelectedIds(ids: string[]): void;
// };

// /** ==== Internal UI types ==== */
// type UiFee = {
//   id: string;
//   title: string;
//   amount: number;
//   taxable: boolean;
//   required: boolean;
//   note?: string;
//   status?: boolean;
// };

// function mapToUi(f: {
//   id: string;
//   name: string;
//   amount: number;
//   taxable?: boolean;
//   required?: boolean;
//   note?: string;
//   status?: boolean;
// }): UiFee {
//   return {
//     id: String(f.id),
//     title: String(f.name || ""),
//     amount: Number(f.amount || 0),
//     taxable: f?.taxable !== false,
//     required: !!f?.required,
//     note: f?.note || "",
//     status: f?.status,
//   };
// }

// function money(n: any) {
//   const v = Number(n || 0);
//   return `$${v.toFixed(2)}`;
// }

// /** ==== Props from parent ==== */
// type Props = {
//   token: string;
//   API?: string;

//   /** created_by / supplier id used by /created-by/:id endpoints */
//   personId: string;

//   /** currently selected shipping address (full object) */
//   shippingAddress: any | null;

//   /** Optional: if you already know tax type, pass it to skip fetch */
//   knownTaxType?: "manual" | "taxcloud" | "geera";

//   /** configuration */
//   isTaxable?: boolean;
//   statusOk?: boolean;
//   persistKey?: string;
//   className?: string;

//   /** optional callbacks */
//   onChange?: (selectedIds: string[], amountSum: number) => void;
//   onPayloadChange?: (payload: FeePayload) => void;
// };

// /** ==== Component (forwardRef) ==== */
// const SpecialFeePicker = React.forwardRef<SpecialFeePickerV2Handle, Props>(function SpecialFeePicker(
//   {
//     token,
//     API = process.env.REACT_APP_API_URL || "",
//     personId,
//     shippingAddress,
//     knownTaxType,
//     isTaxable = true,
//     statusOk = true,
//     persistKey = "specialfee:selected",
//     className = "",
//     onChange,
//     onPayloadChange,
//   },
//   ref
// ) {
//   const [loading, setLoading] = React.useState(false);
// console.log("token", token)
// console.log(personId, "personId")
//   // full objects from server (we keep these to send entire fee objects to backend)
//   const [raw, setRaw] = React.useState<any[]>([]);
//   // simplified for UI
//   const [uiFees, setUiFees] = React.useState<UiFee[]>([]);
//   // controlled selection (initially from localStorage only; we do NOT write back)
//   const [selected, setSelected] = React.useState<string[]>(() => {
//     try {
//       const saved = JSON.parse(localStorage.getItem(persistKey || "") || "[]");
//       return Array.isArray(saved) ? saved.map(String) : [];
//     } catch {
//       return [];
//     }
//   });
//   // rows shown (computed tax rows)
//   const [rows, setRows] = React.useState<SpecialFeeRow[]>([]);
// //   const ensureTaxType = React.useCallback(async () => {
// //     if (knownTaxType) return knownTaxType;
// //     return getActiveTaxType( personId);
// //   }, [knownTaxType, token, personId]);
//   /** Tax type resolver */
//   const ensureTaxType = React.useCallback(async () => {
//     if (knownTaxType) return knownTaxType;
//     // NOTE: pass token + personId (createdBy) to resolve backend tenant type if your helper supports it
//     return getActiveTaxType( personId);
//   }, [knownTaxType, token, personId]);

//   /** Load fees for this user (required + optional) */
//   React.useEffect(() => {
//     let alive = true;
//     (async () => {
//       if (!token || !personId) {
//         setRaw([]); setUiFees([]); return;
//       }
//       setLoading(true);
//       try {
//         console.log("fvbhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhhh")
//         const res = await fetch(`${API}/special-fees/by-user/${personId}`, {
//           headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//         });
//         let data: any[] = [];
//         if (res.ok) data = await res.json().catch(() => []);
//         const filtered = (data || []).filter((f: any) => f?.status !== false);
// console.log("filtered", filtered)
//         const mapped = filtered.map((f: any) =>
//           mapToUi({
//             id: String(f.id || f._id || ""),
//             name: f.name,
//             amount: Number(f.amount || 0),
//             taxable: f?.taxable !== false,
//             required: !!f?.required,
//             note: f?.note || "",
//             status: f?.status,
//           })
//         );

//         // auto-select "required" fees (merge with previous selection)
//         const requiredIds = mapped.filter((f) => f.required).map((f) => f.id);
//         const merged = Array.from(new Set([...(selected || []), ...requiredIds]));

//         if (!alive) return;
//         setRaw(filtered);
//         setUiFees(mapped);
//         setSelected(merged);

//         // notify parent about selected + amount sum
//         const total = mapped
//           .filter((f) => merged.includes(f.id))
//           .reduce((n, f) => n + Number(f.amount || 0), 0);
//         onChange?.(merged, total);
//       } finally {
//         if (alive) setLoading(false);
//       }
//     })();
//     return () => { alive = false; };
//   }, [token, personId]);

//   /** Compute taxes whenever selection OR shipping address changes */
//   const computeRows = React.useCallback(
//     async (ids: string[]) => {
//       const picked = uiFees.filter((f) => ids.includes(f.id));
//       if (picked.length === 0) {
//         setRows([]);
//         onPayloadChange?.({
//           specialfee: [],
//           selected_fee_ids: [],
//           total_amount: 0,
//           total_tax_amount: 0,
//           total_tax_percentage: 0,
//         });
//         return;
//       }

//       const tt = await ensureTaxType();
//       const canTax = !!(isTaxable && statusOk && shippingAddress);

//       // Build inputs from FULL RAW OBJECTS (send entire objects to backend)
//       const rawById = new Map(raw.map((r) => [String(r.id || r._id || ""), r]));
//       const inputs: SpecialFeeInput[] = picked.map((p) => {
//         const full = rawById.get(p.id) || {
//           id: p.id,
//           name: p.title,
//           amount: p.amount,
//           taxable: p.taxable,
//         };
//         return {
//           fee: full,                      // entire selected fee object
//           address: shippingAddress,       // selected shipping address (full object)
//           createdById: personId,          // /created-by/:id
//         };
//       });

//       // If we cannot compute tax (no address or disabled), return zero-tax rows from UI amounts
//       if (!canTax) {
//         const zeroRows: SpecialFeeRow[] = inputs.map(({ fee }) => ({
//           specialfee_id: String(fee.id || fee._id || ""),
//           specialfee_name: String(fee.name || ""),
//           specialfee_amount: Number(fee.amount || 0),
//           specialfee_tax_percentage: 0,
//           specialfee_tax_amount: 0,
//           specialfee_taxable: fee?.taxable !== false,
//         }));
//         setRows(zeroRows);
//         onPayloadChange?.({
//           specialfee: zeroRows,
//           selected_fee_ids: ids,
//           total_amount: zeroRows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//           total_tax_amount: 0,
//           total_tax_percentage: 0,
//         });
//         return;
//       }

//       try {
//         const result: SpecialFeeCalcResult[] = await calspecicalfee(token, tt as any, inputs);

//         const normalized: SpecialFeeRow[] = (Array.isArray(result) ? result : []).map((r) => ({
//           specialfee_id: String(r.specialfee_id ?? ""),
//           specialfee_name: String(r.specialfee_name ?? ""),
//           specialfee_amount: Number(r.specialfee_amount ?? 0),
//           specialfee_tax_percentage: Number(r.specialfee_tax_percentage ?? 0),
//           specialfee_tax_amount: Number(r.specialfee_tax_amount ?? 0),
//           specialfee_taxable: r?.specialfee_taxable !== false,
//         }));

//         setRows(normalized);
//         onPayloadChange?.({
//           specialfee: normalized,
//           selected_fee_ids: ids,
//           total_amount: normalized.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//           total_tax_amount: normalized.reduce((s, r) => s + Number(r.specialfee_tax_amount || 0), 0),
//           total_tax_percentage: 0,
//         });
//       } catch {
//         // fallback to zero-tax on error
//         const zeroRows: SpecialFeeRow[] = inputs.map(({ fee }) => ({
//           specialfee_id: String(fee.id || fee._id || ""),
//           specialfee_name: String(fee.name || ""),
//           specialfee_amount: Number(fee.amount || 0),
//           specialfee_tax_percentage: 0,
//           specialfee_tax_amount: 0,
//           specialfee_taxable: fee?.taxable !== false,
//         }));
//         setRows(zeroRows);
//         onPayloadChange?.({
//           specialfee: zeroRows,
//           selected_fee_ids: ids,
//           total_amount: zeroRows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//           total_tax_amount: 0,
//           total_tax_percentage: 0,
//         });
//       }
//     },
//     [uiFees, raw, isTaxable, statusOk, shippingAddress, ensureTaxType, token, personId, onPayloadChange]
//   );

//   /** Recompute when selection or address changes */
//   React.useEffect(() => {
//     computeRows(selected);
//   }, [selected, shippingAddress, computeRows]);

//   /** Expose ref handle to parent */
//   React.useImperativeHandle(
//     ref,
//     () => ({
//       getPayload(): FeePayload {
//         const payload: FeePayload = {
//           specialfee: rows,
//           selected_fee_ids: selected,
//           total_amount: rows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0),
//           total_tax_amount: rows.reduce((s, r) => s + Number(r.specialfee_tax_amount || 0), 0),
//           total_tax_percentage: 0,
//         };
//         return payload;
//       },
//       setSelectedIds(ids: string[]) {
//         setSelected(Array.from(new Set((ids || []).map(String))));
//       },
//     }),
//     [rows, selected]
//   );

//   /** UI — simple “muted” layout */
//   return (
//     <div className={className}>
//       <div className="d-flex align-items-center justify-content-between mb-2">
//         <h6 className="mb-0">Special Fees</h6>
//         {loading && <small className="text-muted">loading…</small>}
//       </div>

//       {uiFees.length === 0 && <div className="text-muted small">No special fees</div>}

//       {uiFees.map((f) => {
//         const checked = selected.includes(f.id);
//         return (
//           <label key={f.id} className="d-flex justify-content-between align-items-center py-1">
//             <span className="d-flex align-items-center gap-2">
//               <input
//                 type="checkbox"
//                 checked={checked}
//                 disabled={f.required}
//                 onChange={(e) => {
//                   const on = e.target.checked;
//                   setSelected((prev) => {
//                     const next = on ? Array.from(new Set([...prev, f.id])) : prev.filter((x) => x !== f.id);
//                     const amountSum = uiFees
//                       .filter((u) => next.includes(u.id))
//                       .reduce((s, u) => s + Number(u.amount || 0), 0);
//                     onChange?.(next, amountSum);
//                     return next;
//                   });
//                 }}
//               />
//               <span>{f.title}</span>
//             </span>
//             <span className="text-nowrap">{money(f.amount)}</span>
//           </label>
//         );
//       })}

//       {rows.length > 0 && (
//         <div className="mt-2 small">
//           {rows.map((r) => (
//             <div key={r.specialfee_id} className="d-flex justify-content-between">
//               <span>{r.specialfee_name}</span>
//               <span>
//                 {money(r.specialfee_amount)}{" "}
//                 <span className="text-muted">
//                   (+{money(r.specialfee_tax_amount)} / {Number(r.specialfee_tax_percentage || 0).toFixed(2)}%)
//                 </span>
//               </span>
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   );
// });

// export default SpecialFeePicker;
